import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import SettingsIcon from "@material-ui/icons/Settings";
import CheckCircle from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Clearfix from "components/Clearfix/Clearfix.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import Snackbar from "components/Snackbar/Snackbar.js";

import styles from "assets/jss/material-dashboard-pro-react/views/userProfileStyles.js";

const useStyles = makeStyles(styles);

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

const GET_SETTINGS = `
  query GetSettings {
    getSettings {
      id
      shopName
      printInvoiceHeader
      printInvoiceFooter
    }
  }
`;

const UPDATE_SETTINGS = `
  mutation UpdateSettings($data: UpdateSettingsDto!) {
    updateSettings(data: $data)
  }
`;

function gql(query, variables) {
  const token = localStorage.getItem("token");
  return fetch(GRAPHQL_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
    credentials: "include",
    body: JSON.stringify({ query, variables }),
  }).then((r) => r.json());
}

export default function SettingsPage() {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    shopName: "",
    printInvoiceHeader: "",
    printInvoiceFooter: "",
  });
  const [notification, setNotification] = useState({ open: false, message: "", color: "success" });

  useEffect(() => {
    gql(GET_SETTINGS)
      .then(({ data, errors }) => {
        if (errors || !data?.getSettings) return;
        const s = data.getSettings;
        setForm({
          shopName: s.shopName || "",
          printInvoiceHeader: s.printInvoiceHeader || "",
          printInvoiceFooter: s.printInvoiceFooter || "",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    const { data, errors } = await gql(UPDATE_SETTINGS, { data: form });
    setSaving(false);
    if (errors || data?.updateSettings === false) {
      setNotification({ open: true, message: errors?.[0]?.message || "Failed to save settings", color: "danger" });
    } else {
      setNotification({ open: true, message: "Settings saved successfully", color: "success" });
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: 80 }}>
        <CircularProgress color="secondary" />
      </div>
    );
  }

  return (
    <div>
      <Snackbar
        place="tr"
        color={notification.color}
        icon={notification.color === "success" ? CheckCircle : ErrorIcon}
        message={notification.message}
        open={notification.open}
        closeNotification={() => setNotification((n) => ({ ...n, open: false }))}
        close
      />
      <GridContainer justifyContent="center">
        <GridItem xs={12} sm={12} md={8}>
          <Card>
            <CardHeader color="rose" icon>
              <CardIcon color="rose">
                <SettingsIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Settings</h4>
            </CardHeader>
            <CardBody>
              <GridContainer>
                <GridItem xs={12}>
                  <CustomInput
                    labelText="Shop Name"
                    id="shop-name"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: form.shopName,
                      onChange: handleChange("shopName"),
                    }}
                  />
                </GridItem>
                <GridItem xs={12}>
                  <CustomInput
                    labelText="Print Invoice Header"
                    id="print-invoice-header"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: form.printInvoiceHeader,
                      onChange: handleChange("printInvoiceHeader"),
                      multiline: true,
                      rows: 3,
                    }}
                  />
                </GridItem>
                <GridItem xs={12}>
                  <CustomInput
                    labelText="Print Invoice Footer"
                    id="print-invoice-footer"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: form.printInvoiceFooter,
                      onChange: handleChange("printInvoiceFooter"),
                      multiline: true,
                      rows: 3,
                    }}
                  />
                </GridItem>
              </GridContainer>
              <Button color="rose" className={classes.updateProfileButton} onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Settings"}
              </Button>
              <Clearfix />
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
